import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import NEXT_VISIT_FILTER from 'constants/sms/nextVisit.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import call from 'utils/functions/call.util';
import C from 'utils/values/constant.util';

import CN from './Filter.module.scss';

const NextVisitFilter = ({ filtered, onFilter }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [selected, setSelected] = useState(filtered);
  const { val: isRendered, tip: toggle, nay: close } = useTip(false);

  useEffect(() => {
    setSelected(filtered);
  }, [filtered]);

  const handleApply = useCallback(() => {
    call('NextVisitFilter.onFilter', onFilter, selected);
    close();
  }, [onFilter, selected, close]);

  const handleClear = useCallback(() => {
    setSelected(null);
    call('NextVisitFilter.onFilter', onFilter, null);
  }, [onFilter]);

  const NEXT_VISIT = C({
    [NEXT_VISIT_FILTER.ANY_DATE]: {
      key: NEXT_VISIT_FILTER.ANY_DATE,
      option: t('clientsEmailPage.nextVisitFilter.anyDateOption'),
      label: t('clientsEmailPage.nextVisitFilter.anyDateLabel'),
    },
    [NEXT_VISIT_FILTER.NEXT_7_DAYS]: {
      key: NEXT_VISIT_FILTER.NEXT_7_DAYS,
      option: t('clientsEmailPage.nextVisitFilter.nextWeekOption'),
      label: t('clientsEmailPage.nextVisitFilter.nextWeekLabel'),
    },
    [NEXT_VISIT_FILTER.NO_FUTURE]: {
      key: NEXT_VISIT_FILTER.NO_FUTURE,
      option: t('clientsEmailPage.nextVisitFilter.noDateOption'),
      label: t('clientsEmailPage.nextVisitFilter.noDateLabel'),
    },
  });

  const NEXT_VISIT_OPTIONS = Object.values(NEXT_VISIT).map(({ key, option }) => ({ key, option }));

  return (
    <div
      data-bem="NextVisitFilter"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div
        onClick={toggle}
        className={cn({
          [CN.filterButton]: true,
          [CN.isSelected]: selected,
        })}
      >
        {selected ? NEXT_VISIT[selected].label : t('clientsEmailPage.nextVisitFilter.filterDefaultValue')}
        <Icon variant="arrowTriangle" width="xs" />
      </div>
      {isRendered && (
        <DropdownFoldout width="lg" position="bottom" onClickOutside={close}>
          <DropdownFoldout.Header>
            <div className={CN.dropdownTitle}>{t('clientsEmailPage.nextVisitFilter.title')}</div>
          </DropdownFoldout.Header>
          <DropdownFoldout.Body>
            <div className={CN.dropdownContent}>
              {NEXT_VISIT_OPTIONS.map(({ key, option }) => (
                <div key={key} className={CN.dropdownItem} onClick={() => setSelected(key)}>
                  <span>{option}</span>
                  <Icon variant="radio" selected={selected === key} width="md" />
                </div>
              ))}
            </div>
          </DropdownFoldout.Body>
          <DropdownFoldout.Footer>
            <div className={CN.dropdownFooterContainer}>
              <Button variant="outline" onClick={handleClear} tag="NextVisitFilter__clear">
                {t('common.buttons.clear').toUpperCase()}
              </Button>
              <Button variant="primary" onClick={handleApply} tag="NextVisitFilter__done">
                {t('common.buttons.apply').toUpperCase()}
              </Button>
            </div>
          </DropdownFoldout.Footer>
        </DropdownFoldout>
      )}
    </div>
  );
};

export default NextVisitFilter;
